/**
 * useTaskTemplates — the React binding for the Task Templates feature.
 *
 * Strict layering: the page calls THIS hook; the hook calls
 * {@link TaskTemplateStore}; the store calls the api facade. The page never
 * imports the store or the api directly. Mirrors {@link ../preplanning/usePrePlanning}.
 *
 * Exposes the sorted template list (default first), a selected template with its
 * hydrated Markdown content, create/update/remove/setDefault mutations, an
 * idempotent {@link UseTaskTemplatesResult.ensureDefault} seeder, and a
 * {@link UseTaskTemplatesResult.loadContent} helper other features (e.g. the
 * pre-plan editor's "Start from template" picker) use to prefill from a template.
 * Selection hydrates the full S3 body on demand (the list holds only pointer-row
 * summaries). Tenant scoping is automatic (the store routes by the active team
 * bundle).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TaskTemplateStore } from './taskTemplateStore';
import { sortTaskTemplates } from './taskTemplateModel';
import type { TaskTemplate, TaskTemplateDraft, TaskTemplateSummary } from './types';

export interface UseTaskTemplatesResult {
  /** All non-deleted summaries, default-first then A→Z. */
  templates: TaskTemplateSummary[];
  /** The currently selected summary id, or null. */
  selectedId: string | null;
  /** The hydrated full record for the selected summary, or null. */
  selected: TaskTemplate | null;
  /** True while the selected record's S3 body is being hydrated. */
  selecting: boolean;
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Selects a summary by id (hydrates its content); pass null to clear. */
  select: (id: string | null) => Promise<void>;
  get: (id: string) => Promise<TaskTemplate | undefined>;
  /** Loads a template's Markdown content by id (empty string when missing). */
  loadContent: (id: string) => Promise<string>;
  create: (draft: TaskTemplateDraft) => Promise<TaskTemplate>;
  update: (id: string, draft: TaskTemplateDraft) => Promise<TaskTemplate | undefined>;
  remove: (id: string) => Promise<void>;
  setDefault: (id: string) => Promise<void>;
  /** Idempotently seeds the built-in default template when none exist. */
  ensureDefault: () => Promise<void>;
}

export function useTaskTemplates(): UseTaskTemplatesResult {
  const api = useApi();
  const store = useMemo(() => new TaskTemplateStore(api), [api]);

  const [templates, setTemplates] = useState<TaskTemplateSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<TaskTemplate | null>(null);
  const [selecting, setSelecting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, identity] = await Promise.all([
        store.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setTemplates(sortTaskTemplates(rows));
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [store, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const select = useCallback(
    async (id: string | null) => {
      if (!id) {
        setSelectedId(null);
        setSelected(null);
        return;
      }
      setSelectedId(id);
      setSelecting(true);
      try {
        const full = await store.get(id);
        setSelected(full ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load template.');
        setSelected(null);
      } finally {
        setSelecting(false);
      }
    },
    [store],
  );

  const get = useCallback((id: string) => store.get(id), [store]);

  const loadContent = useCallback(
    async (id: string) => {
      const full = await store.get(id);
      return full?.content ?? '';
    },
    [store],
  );

  const create = useCallback(
    async (draft: TaskTemplateDraft) => {
      const saved = await store.create(draft);
      await refresh();
      await select(saved.id);
      return saved;
    },
    [store, refresh, select],
  );

  const update = useCallback(
    async (id: string, draft: TaskTemplateDraft) => {
      const existing = await store.get(id);
      if (!existing) return undefined;
      const saved = await store.applyDraft(existing, draft);
      await refresh();
      if (selectedId === id) setSelected(saved);
      return saved;
    },
    [store, refresh, selectedId],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      if (selectedId === id) {
        setSelectedId(null);
        setSelected(null);
      }
      await refresh();
    },
    [store, refresh, selectedId],
  );

  const setDefault = useCallback(
    async (id: string) => {
      await store.setDefault(id);
      await refresh();
      if (selectedId === id) await select(id);
    },
    [store, refresh, select, selectedId],
  );

  // Idempotently seed the built-in default template once per mounted hook.
  const seededRef = useRef(false);
  const ensureDefault = useCallback(async () => {
    if (seededRef.current) return;
    seededRef.current = true;
    const created = await store.ensureDefault();
    if (created) await refresh();
  }, [store, refresh]);

  return {
    templates,
    selectedId,
    selected,
    selecting,
    currentAlias,
    loading,
    error,
    refresh,
    select,
    get,
    loadContent,
    create,
    update,
    remove,
    setDefault,
    ensureDefault,
  };
}
